import React, { useEffect, useState } from 'react';
import * as Routes from '@/modules/routes';
import Axios from 'axios';
import Spinner from '@/components/common/Spinner';
import Tooltip from '@/components/ui/Tooltip';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import { compareGlossaryTerms } from '@/utils/sortGlossaryTerms';
import VocabSelectModal from './VocabSelectModal';
import { GlossaryTermModel, SlideObject } from '../../../types';
import azIcon from './icon.svg';
import VocabList from './VocabList';
import VocabCard from './VocabCard';
import VocabTable from './VocabTable';

interface SlideObjectVocabProps {
  interactable: boolean;
  modalManager: ModalManager;
  slideObject: SlideObject;
}

const SlideObjectVocab = ({ interactable, modalManager, slideObject }: SlideObjectVocabProps) => {
  const [loading, setLoading] = useState(true);
  const [linkedVocab, setLinkedVocab] = useState<GlossaryTermModel[]>([]);
  const isPresentMode = useSlideShowContext(state => state.isPresentMode);
  const locale = useSlideShowContext(state => state.locale);

  useEffect(() => {
    setLoading(true);
    Axios.get(Routes.plato_api_get_glossary_term_models_slide_objects_path(), { params: { id: slideObject.id } })
      .then((res) => {
        setLinkedVocab(res.data.data.filter(term => term.glossary_term.kind !== 'Academic'));
      })
      .finally(() => setLoading(false));
  }, [slideObject.id, slideObject]);

  if (loading) return <Spinner />;

  const displayedTerm = (vocab: GlossaryTermModel) => (
    locale === 'en' ? vocab.glossary_term.term_en : vocab.glossary_term.term_es
  );
  const sortedVocab = [...linkedVocab].sort((a, b) => compareGlossaryTerms(displayedTerm(a), displayedTerm(b), locale));

  const hasVocab = linkedVocab.length > 0;
  const showCard = hasVocab && slideObject.modelType === 'GlossaryTermCard';
  const showList = hasVocab && slideObject.modelType === 'GlossaryTermList';
  const showTable = hasVocab && slideObject.modelType === 'GlossaryTermTable';

  return (
    <>
      {!isPresentMode && (
        <Tooltip.Provider>
          <Tooltip>
            <Tooltip.Trigger asChild>
              <img
                alt="Vocabulary icon"
                className="tw-absolute tw-top-1 tw-left-1 tw-bg-white tw-rounded"
                src={azIcon}
              />
            </Tooltip.Trigger>
            <Tooltip.Content
              align="start"
              className="tw-bg-white tw-shadow tw-rounded-sm tw-text-sm tw-py-1 tw-px-2 tw-outline tw-outline-1
                tw-outline-gray-300"
              side="top"
            >
              To make changes, please edit the glossary term itself. Edits cannot be made directly within the slideshow.
            </Tooltip.Content>
          </Tooltip>
        </Tooltip.Provider>
      )}
      {showCard && (
        <VocabCard
          glossaryTerm={sortedVocab[0].glossary_term}
          slideObject={slideObject}
        />
      )}
      {showList && (
        <VocabList
          linkedVocab={sortedVocab}
          slideObject={slideObject}
        />
      )}
      {showTable && (
        <VocabTable
          linkedVocab={sortedVocab}
          slideObject={slideObject}
        />
      )}
      {interactable && modalManager.isOpen && (
        <VocabSelectModal
          linkedVocab={sortedVocab}
          modalManager={modalManager}
          setLinkedVocab={setLinkedVocab}
          slideObject={slideObject}
        />
      )}
    </>
  );
};

export default SlideObjectVocab;
